import { Callout } from '@piggybank/core';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import snakeCase from 'lodash/snakeCase';
import T from 'prop-types';
import React, { useEffect, useState } from 'react';
import { MarkdownTranslation } from '../../Markdown';
import { TranslationScope } from '../../context';
import { formNameFromKeyPath, localizationKeyPath } from '../../utils';

const Paragraph = props => <div {...props} />;
const allowedComponents = { Callout, Paragraph };

const valueInMatcher = (matcher, value, positiveMatch = true) => {
  if (isArray(matcher)) {
    for (const item of matcher) {
      if (item.not) {
        const match = valueInMatcher(item.not, value, false);
        if (match) return `not_${match}`;
      }

      if (isArray(item)) {
        const [subMatcherName, subMatcher] = item;
        const match = valueInMatcher(subMatcher, value, positiveMatch);

        if (match) return subMatcherName;
      }

      if ((item === value) === positiveMatch) return item;
    }
  }

  return null;
};

const parseAppendConfig = (appendConfig = [], fieldValue, keyPath) =>
  appendConfig.reduce(
    (acc, { component: componentName = 'Paragraph', props = {}, on }) => {
      const component = allowedComponents[componentName];
      const appendValue = valueInMatcher(on, fieldValue);

      if (component && (!on || appendValue)) {
        return [
          ...acc,
          {
            componentName,
            component,
            props,
            value: appendValue,
            keyPath,
          },
        ];
      }

      return acc;
    },
    []
  );

const AppendUI = ({ append = {}, context, formValues, renderChildren }) => {
  const [appendComponents, setAppendComponents] = useState([]);

  const fieldValue = get(
    formValues,
    formNameFromKeyPath(context.keyPath),
    null
  );

  useEffect(() => {
    setAppendComponents(
      parseAppendConfig(
        isArray(append) ? append : [append],
        fieldValue,
        context.keyPath
      )
    );
  }, [fieldValue]);

  return (
    <>
      {renderChildren({
        context: { ...context, parent: '@@' },
        formValues,
      })}

      {!isEmpty(appendComponents) &&
        appendComponents.map(
          (
            { component: Component, componentName, keyPath, props, value },
            index
          ) => (
            <TranslationScope key={index} path={localizationKeyPath(keyPath)}>
              <Component {...props}>
                <MarkdownTranslation
                  formValues={formValues}
                  path={[
                    `append${componentName}`,
                    value ? snakeCase(value).toUpperCase() : 'message',
                  ].join('.')}
                />
              </Component>
            </TranslationScope>
          )
        )}
    </>
  );
};

const appendPropType = T.shape({
  component: T.oneOf(['Callout', 'Paragraph']),
  props: T.object,
  on: T.oneOfType([
    T.string,
    T.bool,
    T.array,
    T.shape({
      not: T.oneOfType([T.string, T.bool, T.array]),
    }),
  ]),
});

AppendUI.propTypes = {
  context: T.object,
  formValues: T.object,
  renderChildren: T.func,
  append: T.oneOfType([appendPropType, T.arrayOf(appendPropType)]),
};

export default AppendUI;
